<script setup lang="ts">
/**
 * Filter bar that is not tied to a table: one filter state narrows a list, a
 * board and a calendar view alike. The bar only holds the state — the caller
 * filters its own rows with `applyFilterBar` / `matchesFilterBar`.
 *
 * Every field („Status“, „Projekt“, „Bearbeiter“ …) is a button that opens a
 * popover with one check box per value and shows how many of its values are
 * ticked. Values of one field combine with OR; with two or more active fields
 * the AND/OR switch (`v-model:mode`) decides how the fields combine. Active
 * values are listed as removable chips, next to „Alle löschen“ (which also
 * resets the mode to AND) and „Als Ansicht speichern“. Saved views (`views`)
 * sit in a row of their own: a click applies one, the × deletes it, and the
 * view matching the current state is marked.
 *
 * Nothing is persisted here. `v-model:filters` / `v-model:mode` carry the
 * state; `save-view` hands over name, filters and mode, and the caller stores
 * the view and passes it back through `views`. Applying a view sets both
 * models itself and emits `apply-view`; deleting one only emits `delete-view`.
 * Value counts (`option.count`) come from the caller: `countFilterOptions`
 * fills them from its rows.
 *
 * Keyboard: field buttons are plain buttons. An open popover takes the focus
 * (its search box, else the first value); ↑/↓, Home and End walk the values,
 * Space ticks one, Escape closes and returns to the field button. A chip goes
 * with its × or Delete/Backspace, and the focus moves on to the next chip.
 */
import { computed, nextTick, ref, useId, useTemplateRef, watch } from 'vue';
import type { ComponentPublicInstance } from 'vue';
import { CheckmarkIcon, ChevronDownIcon, FilterDismissIcon, SaveIcon } from '@blpde/nx-icons';
import NxBadge from '../Badge/NxBadge.vue';
import NxButton from '../Button/NxButton.vue';
import NxChip from '../Chip/NxChip.vue';
import NxChipGroup from '../Chip/NxChipGroup.vue';
import NxDialog from '../Dialog/NxDialog.vue';
import NxCheckbox from '../Inputs/NxCheckbox.vue';
import NxLabel from '../Inputs/NxLabel.vue';
import NxSearchInput from '../Inputs/NxSearchInput.vue';
import NxTextInput from '../Inputs/NxTextInput.vue';
import NxPopover from '../Popover/NxPopover.vue';
import NxToggleGroup from '../ToggleGroup/NxToggleGroup.vue';
import {
  clearFilterField,
  isFilterViewActive,
  normalizeFilters,
  removeFilterValue,
  toggleFilterValue,
  type NxFilterBarField,
  type NxFilterBarFilters,
  type NxFilterBarMode,
  type NxFilterBarOption,
  type NxFilterValue,
  type NxFilterView,
  type NxFilterViewDraft,
} from './filterBar';

const props = withDefaults(
  defineProps<{
    fields: NxFilterBarField[];
    /** Saved views, shown as chips. The caller stores them (`save-view`, `delete-view`). */
    views?: NxFilterView[];
    /** Offer „Als Ansicht speichern“. */
    savable?: boolean;
    disabled?: boolean;
    /** Accessible name of the whole bar. */
    ariaLabel?: string;
    /** Accessible name of a field button, from its label and its number of ticked values. */
    fieldLabel?: (label: string, count: number) => string;
    modeLabel?: string;
    andLabel?: string;
    orLabel?: string;
    /** Why the switch is off below two active fields (tooltip + description). */
    modeInactiveHint?: string;
    /** What the chosen mode does; shown from two active fields on. */
    modeHint?: (mode: NxFilterBarMode, label: string) => string;
    searchPlaceholder?: string;
    /** Shown in a popover without (matching) values. */
    noOptionsLabel?: string;
    resetFieldLabel?: string;
    doneLabel?: string;
    /** Accessible name of the chip group. */
    activeFiltersLabel?: string;
    /** Accessible name of a chip's ×. */
    removeFilterLabel?: (field: string, value: string) => string;
    clearAllLabel?: string;
    saveViewLabel?: string;
    viewsLabel?: string;
    /** Screen-reader suffix of the view that matches the current state. */
    activeViewLabel?: string;
    /** Accessible name of a view chip's ×. */
    deleteViewLabel?: (name: string) => string;
    saveDialogTitle?: string;
    viewNameLabel?: string;
    viewNamePlaceholder?: string;
    /** Caption of the filter summary in the save dialog. */
    viewSummaryLabel?: string;
    saveLabel?: string;
    cancelLabel?: string;
  }>(),
  {
    views: () => [],
    savable: true,
    disabled: false,
    ariaLabel: 'Filter',
    fieldLabel: (label: string, count: number) =>
      count > 0 ? `${label}, ${count} ausgewählt` : label,
    modeLabel: 'Verknüpfung',
    andLabel: 'UND',
    orLabel: 'ODER',
    modeInactiveHint: 'Die Verknüpfung wirkt erst ab zwei aktiven Filtern.',
    modeHint: (mode: NxFilterBarMode, label: string) =>
      mode === 'and'
        ? `${label}: nur Einträge, die alle gesetzten Filter erfüllen.`
        : `${label}: Einträge, die mindestens einen der gesetzten Filter erfüllen.`,
    searchPlaceholder: 'Suche',
    noOptionsLabel: 'Keine Werte',
    resetFieldLabel: 'Zurücksetzen',
    doneLabel: 'Fertig',
    activeFiltersLabel: 'Aktive Filter',
    removeFilterLabel: (field: string, value: string) => `Filter „${field}: ${value}“ entfernen`,
    clearAllLabel: 'Alle löschen',
    saveViewLabel: 'Als Ansicht speichern',
    viewsLabel: 'Gespeicherte Ansichten',
    activeViewLabel: 'aktiv',
    deleteViewLabel: (name: string) => `Ansicht „${name}“ löschen`,
    saveDialogTitle: 'Ansicht speichern',
    viewNameLabel: 'Name der Ansicht',
    viewNamePlaceholder: 'z. B. Meine offenen · hohe Prio',
    viewSummaryLabel: 'Enthält',
    saveLabel: 'Speichern',
    cancelLabel: 'Abbrechen',
  }
);

/** Ticked values per field key. */
const filters = defineModel<NxFilterBarFilters>('filters', { default: () => ({}) });
/** How the active fields combine. */
const mode = defineModel<NxFilterBarMode>('mode', { default: 'and' });

const emit = defineEmits<{
  'save-view': [view: NxFilterViewDraft];
  'apply-view': [view: NxFilterView];
  'delete-view': [view: NxFilterView];
}>();

defineSlots<{
  /** Before the field buttons, e.g. a search field. */
  leading?(): unknown;
  /** At the end of the first row, e.g. the switch between list, board and calendar. */
  actions?(): unknown;
  /** One value in a field's popover (default: its label). */
  option?(props: {
    field: NxFilterBarField;
    option: NxFilterBarOption;
    selected: boolean;
  }): unknown;
}>();

type TemplateRef = Element | ComponentPublicInstance | null;

type ActiveChip = {
  id: string;
  key: string;
  value: NxFilterValue;
  field: string;
  label: string;
};

const hintId = useId();
const openField = ref<string | null>(null);
/** Search text of the open popover (only one is open at a time). */
const query = ref('');
const triggers = new Map<string, HTMLElement>();
const panels = new Map<string, HTMLElement>();
const activeRef = useTemplateRef<HTMLElement>('active-ref');
const viewsRef = useTemplateRef<HTMLElement>('views-ref');
const nameInput = useTemplateRef<InstanceType<typeof NxTextInput>>('name-input');

const current = computed(() => normalizeFilters(filters.value));
const fieldIndex = computed(() => new Map(props.fields.map((field) => [field.key, field])));

/** Ticked value keys per active field. */
const selection = computed(
  () =>
    new Map(
      Object.entries(current.value).map(([key, values]) => [key, new Set(values.map(String))])
    )
);
const activeCount = computed(() => selection.value.size);
const modeActive = computed(() => activeCount.value >= 2);
/** AND/OR means nothing for a bar with a single field. */
const showMode = computed(() => props.fields.length > 1 || modeActive.value);

const countOf = (key: string) => selection.value.get(key)?.size ?? 0;
const isSelected = (key: string, value: NxFilterValue) =>
  selection.value.get(key)?.has(String(value)) ?? false;

function labelOf(key: string, value: NxFilterValue): string {
  const option = fieldIndex.value
    .get(key)
    ?.options.find((candidate) => String(candidate.value) === String(value));
  return option?.label ?? String(value);
}

/** Chips in the order of the fields; keys the bar has no field for go last. */
const chips = computed<ActiveChip[]>(() => {
  const keys = [
    ...new Set([
      ...props.fields.map((field) => field.key).filter((key) => key in current.value),
      ...Object.keys(current.value),
    ]),
  ];
  return keys.flatMap((key) =>
    current.value[key].map((value) => ({
      id: JSON.stringify([key, String(value)]),
      key,
      value,
      field: fieldIndex.value.get(key)?.label ?? key,
      label: labelOf(key, value),
    }))
  );
});

const modeLabelOf = (value: NxFilterBarMode) => (value === 'and' ? props.andLabel : props.orLabel);

const modeOptions = computed(() => {
  const tooltip = modeActive.value ? undefined : props.modeInactiveHint;
  return [
    { value: 'and' as const, label: props.andLabel, tooltip },
    { value: 'or' as const, label: props.orLabel, tooltip },
  ];
});

const hintText = computed(() =>
  modeActive.value ? props.modeHint(mode.value, modeLabelOf(mode.value)) : props.modeInactiveHint
);

const activeViews = computed(
  () =>
    new Set(
      props.views
        .filter((view) => isFilterViewActive(view, current.value, mode.value))
        .map((view) => view.id)
    )
);

/** Summary in the save dialog: one line per field, plus the mode where it counts. */
const summaryLines = computed(() => {
  const byField = new Map<string, { field: string; labels: string[] }>();
  for (const chip of chips.value) {
    const line = byField.get(chip.key) ?? { field: chip.field, labels: [] };
    line.labels.push(chip.label);
    byField.set(chip.key, line);
  }
  const lines = [...byField.values()].map(({ field, labels }) => `${field}: ${labels.join(', ')}`);
  if (modeActive.value) lines.push(`${props.modeLabel}: ${modeLabelOf(mode.value)}`);
  return lines;
});

// ── Refs ───────────────────────────────────────────────────────────────────
function elementOf(target: TemplateRef): HTMLElement | null {
  if (!target) return null;
  return (target instanceof Element ? target : target.$el) as HTMLElement;
}

function setTrigger(key: string, target: TemplateRef) {
  const element = elementOf(target);
  if (element) triggers.set(key, element);
  else triggers.delete(key);
}

function setPanel(key: string, target: TemplateRef) {
  const element = elementOf(target);
  if (element) panels.set(key, element);
  else panels.delete(key);
}

const firstTrigger = () => (props.fields[0] ? triggers.get(props.fields[0].key) : undefined);

// ── Field popovers ─────────────────────────────────────────────────────────
function setFieldOpen(key: string, open: boolean) {
  if (open) {
    if (!props.disabled) openField.value = key;
  } else if (openField.value === key) {
    openField.value = null;
  }
}

function closeField(key: string) {
  setFieldOpen(key, false);
  triggers.get(key)?.focus();
}

function firstValueBox(key: string): HTMLElement | null {
  return panels.get(key)?.querySelector<HTMLElement>('[role="checkbox"]:not(:disabled)') ?? null;
}

watch(
  openField,
  async (key) => {
    query.value = '';
    if (key === null) return;
    // The popover shows its panel (`showPopover`) in a tick of its own, queued
    // before this post-flush watcher — focusing any earlier would hit a hidden
    // element.
    await nextTick();
    if (openField.value !== key) return;
    const panel = panels.get(key);
    (panel?.querySelector<HTMLElement>('input') ?? firstValueBox(key))?.focus();
  },
  { flush: 'post' }
);

watch(
  () => props.disabled,
  (disabled) => {
    if (disabled) openField.value = null;
  }
);

/** Tabbing away from the button and its popover closes the popover. */
function onFocusOut(event: FocusEvent, key: string) {
  const next = event.relatedTarget;
  // No related target: a click on a non-focusable spot inside, or the window
  // lost focus. The popover's own outside-press handling covers real exits.
  if (!(next instanceof Node) || openField.value !== key) return;
  if (panels.get(key)?.contains(next) || triggers.get(key)?.contains(next)) return;
  setFieldOpen(key, false);
}

function onPanelKeydown(event: KeyboardEvent, key: string) {
  const panel = panels.get(key);
  const target = event.target;
  if (!panel || !(target instanceof HTMLElement) || event.isComposing) return;
  const boxes = [...panel.querySelectorAll<HTMLElement>('[role="checkbox"]:not(:disabled)')];

  if (target instanceof HTMLInputElement) {
    // The popover leaves Escape to inner text fields, so the search box owns
    // it: the first press clears the search, the second closes.
    if (event.key === 'Escape') {
      event.preventDefault();
      if (query.value) query.value = '';
      else closeField(key);
    } else if (event.key === 'ArrowDown' && boxes.length > 0) {
      event.preventDefault();
      boxes[0].focus();
    }
    return;
  }

  const index = boxes.indexOf(target);
  if (index < 0) return;
  let next: HTMLElement | null | undefined;
  if (event.key === 'ArrowDown') next = boxes[Math.min(index + 1, boxes.length - 1)];
  else if (event.key === 'ArrowUp')
    next = index > 0 ? boxes[index - 1] : panel.querySelector<HTMLElement>('input');
  else if (event.key === 'Home') next = boxes[0];
  else if (event.key === 'End') next = boxes[boxes.length - 1];
  else return;
  event.preventDefault();
  next?.focus();
}

/**
 * Values of a field for its popover. Ticked values the field does not offer
 * (any more) — from a saved view or from the caller — stay listed, so they can
 * be unticked. The search keeps ticked values in view.
 */
function optionsOf(field: NxFilterBarField): NxFilterBarOption[] {
  const offered = new Set(field.options.map((option) => String(option.value)));
  const foreign = (current.value[field.key] ?? [])
    .filter((value) => !offered.has(String(value)))
    .map((value) => ({ value, label: String(value) }));
  const all = [...field.options, ...foreign];
  const needle = query.value.trim().toLocaleLowerCase();
  if (!field.searchable || !needle) return all;
  return all.filter(
    (option) =>
      option.label.toLocaleLowerCase().includes(needle) || isSelected(field.key, option.value)
  );
}

const optionName = (option: NxFilterBarOption) =>
  option.count === undefined ? option.label : `${option.label} (${option.count})`;

function toggle(key: string, value: NxFilterValue) {
  if (props.disabled) return;
  filters.value = toggleFilterValue(filters.value, key, value);
}

async function resetField(key: string) {
  filters.value = clearFilterField(filters.value, key);
  // The button disables itself — hand the focus to the values instead of <body>.
  await nextTick();
  firstValueBox(key)?.focus();
}

// ── Chips ──────────────────────────────────────────────────────────────────
function openFromChip(key: string) {
  if (fieldIndex.value.has(key)) setFieldOpen(key, true);
}

async function removeChip(chip: ActiveChip, index: number) {
  if (props.disabled) return;
  filters.value = removeFilterValue(filters.value, chip.key, chip.value);
  await nextTick();
  const removes = activeRef.value?.querySelectorAll<HTMLElement>('.nx-chip__remove') ?? [];
  const next = removes.length > 0 ? removes[Math.min(index, removes.length - 1)] : undefined;
  (next ?? triggers.get(chip.key) ?? firstTrigger())?.focus();
}

async function clearAll() {
  openField.value = null;
  filters.value = {};
  mode.value = 'and';
  await nextTick();
  firstTrigger()?.focus();
}

// ── Views ──────────────────────────────────────────────────────────────────
function applyView(view: NxFilterView) {
  if (props.disabled) return;
  openField.value = null;
  filters.value = normalizeFilters(view.filters);
  mode.value = view.mode;
  emit('apply-view', view);
}

async function deleteView(view: NxFilterView, index: number) {
  if (props.disabled || view.deletable === false) return;
  emit('delete-view', view);
  await nextTick();
  // A caller that drops the view at once takes the focused chip with it.
  const focused = document.activeElement;
  if (focused && focused !== document.body) return;
  const chipButtons =
    viewsRef.value?.querySelectorAll<HTMLElement>('button.nx-chip, .nx-chip__label') ?? [];
  const next =
    chipButtons.length > 0 ? chipButtons[Math.min(index, chipButtons.length - 1)] : undefined;
  (next ?? firstTrigger())?.focus();
}

const saveOpen = ref(false);
const viewName = ref('');

function openSaveDialog() {
  viewName.value = '';
  saveOpen.value = true;
}

function focusName() {
  nameInput.value?.inputElement?.focus();
}

function submitView(close?: () => void) {
  const name = viewName.value.trim();
  if (!name) return;
  emit('save-view', { name, filters: normalizeFilters(filters.value), mode: mode.value });
  if (close) close();
  else saveOpen.value = false;
}

function onNameEnter(event: KeyboardEvent) {
  if (event.isComposing) return;
  event.preventDefault();
  submitView();
}
</script>

<template>
  <div class="nx-filter-bar" role="group" :aria-label="ariaLabel">
    <div class="nx-filter-bar__toolbar">
      <slot name="leading" />
      <div class="nx-filter-bar__fields">
        <NxPopover
          v-for="field in fields"
          :key="field.key"
          :open="openField === field.key"
          side="bottom-start"
          lazy
          @update:open="(open: boolean) => setFieldOpen(field.key, open)"
        >
          <template #trigger>
            <NxButton
              :ref="(target: TemplateRef) => setTrigger(field.key, target)"
              class="nx-filter-bar__field"
              :class="{ 'is-active': countOf(field.key) > 0 }"
              variant="outline"
              height="s"
              :disabled="disabled"
              :aria-label="fieldLabel(field.label, countOf(field.key))"
              aria-haspopup="dialog"
              :aria-expanded="openField === field.key"
              @focusout="onFocusOut($event, field.key)"
            >
              <span class="nx-filter-bar__field-label">{{ field.label }}</span>
              <NxBadge v-if="countOf(field.key) > 0" emphasis="solid" size="xs" aria-hidden="true">
                {{ countOf(field.key) }}
              </NxBadge>
              <ChevronDownIcon class="nxt-icon-sm" aria-hidden="true" />
            </NxButton>
          </template>

          <div
            :ref="(target: TemplateRef) => setPanel(field.key, target)"
            class="nx-filter-bar__panel"
            @keydown="onPanelKeydown($event, field.key)"
            @focusout="onFocusOut($event, field.key)"
          >
            <NxSearchInput
              v-if="field.searchable"
              v-model="query"
              height="xs"
              :placeholder="searchPlaceholder"
              :aria-label="searchPlaceholder"
            />
            <div class="nx-filter-bar__options" role="group" :aria-label="field.label">
              <label
                v-for="option in optionsOf(field)"
                :key="String(option.value)"
                class="nx-filter-bar__option"
                :class="{ 'is-empty': option.count === 0 && !isSelected(field.key, option.value) }"
              >
                <NxCheckbox
                  :model-value="isSelected(field.key, option.value)"
                  :aria-label="optionName(option)"
                  :disabled="disabled"
                  @update:model-value="toggle(field.key, option.value)"
                />
                <span class="nx-filter-bar__option-label">
                  <slot
                    name="option"
                    :field="field"
                    :option="option"
                    :selected="isSelected(field.key, option.value)"
                  >
                    {{ option.label }}
                  </slot>
                </span>
                <span
                  v-if="option.count !== undefined"
                  class="nx-filter-bar__option-count"
                  aria-hidden="true"
                >
                  {{ option.count }}
                </span>
              </label>
              <p v-if="optionsOf(field).length === 0" class="nx-filter-bar__empty">
                {{ noOptionsLabel }}
              </p>
            </div>
            <div class="nx-filter-bar__panel-footer">
              <NxButton
                variant="ghost"
                height="xs"
                data-test="reset-field"
                :disabled="disabled || countOf(field.key) === 0"
                @click="resetField(field.key)"
              >
                {{ resetFieldLabel }}
              </NxButton>
              <NxButton
                variant="outline"
                height="xs"
                data-test="done"
                @click="closeField(field.key)"
              >
                {{ doneLabel }}
              </NxButton>
            </div>
          </div>
        </NxPopover>
      </div>
      <NxToggleGroup
        v-if="showMode"
        v-model="mode"
        class="nx-filter-bar__mode"
        :label="modeLabel"
        :options="modeOptions"
        :disabled="disabled || !modeActive"
        height="s"
        :aria-describedby="hintId"
      />
      <slot name="actions" />
    </div>

    <div v-if="chips.length > 0" ref="active-ref" class="nx-filter-bar__active">
      <NxChipGroup :aria-label="activeFiltersLabel">
        <NxChip
          v-for="(chip, index) in chips"
          :key="chip.id"
          removable
          :disabled="disabled"
          :remove-label="removeFilterLabel(chip.field, chip.label)"
          @click="openFromChip(chip.key)"
          @remove="removeChip(chip, index)"
          @keydown.delete.prevent="removeChip(chip, index)"
        >
          <span class="nx-filter-bar__chip-field">{{ `${chip.field}:` }}</span>
          {{ chip.label }}
        </NxChip>
      </NxChipGroup>
      <NxButton
        variant="ghost"
        height="s"
        data-test="clear-all"
        :disabled="disabled"
        @click="clearAll"
      >
        <FilterDismissIcon class="nxt-icon-md" aria-hidden="true" />
        {{ clearAllLabel }}
      </NxButton>
      <NxButton
        v-if="savable"
        variant="ghost"
        height="s"
        data-test="save-view"
        :disabled="disabled"
        @click="openSaveDialog"
      >
        <SaveIcon class="nxt-icon-md" aria-hidden="true" />
        {{ saveViewLabel }}
      </NxButton>
    </div>

    <p
      v-if="showMode"
      :id="hintId"
      class="nx-filter-bar__hint"
      :class="{ 'nx-filter-bar__sr-only': !modeActive }"
    >
      {{ hintText }}
    </p>

    <div v-if="views.length > 0" ref="views-ref" class="nx-filter-bar__views">
      <span class="nx-filter-bar__views-label" aria-hidden="true">{{ viewsLabel }}</span>
      <NxChipGroup :aria-label="viewsLabel">
        <NxChip
          v-for="(view, index) in views"
          :key="view.id"
          :removable="view.deletable !== false"
          :selected="activeViews.has(view.id)"
          :disabled="disabled"
          :remove-label="deleteViewLabel(view.name)"
          @click="applyView(view)"
          @remove="deleteView(view, index)"
          @keydown.delete.prevent="deleteView(view, index)"
        >
          <CheckmarkIcon v-if="activeViews.has(view.id)" class="nxt-icon-sm" aria-hidden="true" />
          {{ view.name }}
          <span v-if="activeViews.has(view.id)" class="nx-filter-bar__sr-only">
            {{ `(${activeViewLabel})` }}
          </span>
        </NxChip>
      </NxChipGroup>
    </div>

    <NxDialog
      v-if="savable"
      v-model:open="saveOpen"
      :title="saveDialogTitle"
      @modal-opened="focusName"
    >
      <template #content>
        <div class="nx-filter-bar__save">
          <NxLabel :text="viewNameLabel">
            <NxTextInput
              ref="name-input"
              v-model="viewName"
              height="s"
              :placeholder="viewNamePlaceholder"
              @keydown.enter="onNameEnter"
            />
          </NxLabel>
          <div v-if="summaryLines.length > 0" class="nx-filter-bar__summary">
            <span class="nx-filter-bar__summary-label">{{ viewSummaryLabel }}</span>
            <ul class="nx-filter-bar__summary-list">
              <li v-for="line in summaryLines" :key="line">{{ line }}</li>
            </ul>
          </div>
        </div>
      </template>
      <template #buttons="{ close }">
        <NxButton data-test="save" :disabled="!viewName.trim()" @click="submitView(close)">
          {{ saveLabel }}
        </NxButton>
        <NxButton variant="outline" @click="close">
          {{ cancelLabel }}
        </NxButton>
      </template>
    </NxDialog>
  </div>
</template>

<style scoped>
.nx-filter-bar {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-xs);
  min-width: 0;
}

.nx-filter-bar__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--nxt-space-xs) var(--nxt-space-sm);
}

.nx-filter-bar__fields {
  display: flex;
  flex: 1 1 auto;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--nxt-space-xs);
  min-width: 0;
}

/* A field with ticked values reads as „on“: the soft brand tint of a selected
   toggle segment, re-pointed on the button's own tokens. */
.nx-filter-bar__field.is-active {
  --nxt-button-bg: var(--nxt-brand-subtle-bg);
  --nxt-button-fg: var(--nxt-brand-subtle-fg);
  --nxt-button-border: var(--nxt-brand-border);
  --nxt-button-bg-hover: var(--nxt-brand-subtle-bg-hover);
  --nxt-button-fg-hover: var(--nxt-brand-subtle-fg);
  --nxt-button-border-hover: var(--nxt-brand-bg);
}
.nx-filter-bar__field[aria-expanded='true'] {
  --nxt-button-border: var(--nxt-brand-bg);
}

.nx-filter-bar__field-label {
  white-space: nowrap;
}

.nx-filter-bar__mode {
  margin-inline-start: auto;
}

/* ── Popover ──────────────────────────────────────────────────────────── */
.nx-filter-bar__panel {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-xs);
  min-width: var(--nxt-panel-width-sm);
  max-width: var(--nxt-panel-width-xl);
}

.nx-filter-bar__options {
  display: flex;
  flex-direction: column;
  max-height: calc(var(--nxt-size-unit) * 16);
  overflow-y: auto;
  overscroll-behavior: contain;
}

.nx-filter-bar__option {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
  border-radius: var(--nxt-radius-sm);
  font-size: var(--nxt-font-size-sm);
  color: var(--nxt-fg);
  cursor: pointer;
}
.nx-filter-bar__option:hover {
  background: var(--nxt-surface-sunken-bg);
}
/* No hits among the rows: still pickable, but quiet. */
.nx-filter-bar__option.is-empty {
  color: var(--nxt-fg-muted);
}

.nx-filter-bar__option-label {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nx-filter-bar__option-count {
  flex: 0 0 auto;
  font-size: var(--nxt-font-size-xs);
  font-variant-numeric: tabular-nums;
  color: var(--nxt-fg-muted);
}

/* `p` and `ul` carry a global type (16px, medium) — pin it where they are used. */
.nx-filter-bar__empty {
  margin: 0;
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-regular);
  line-height: var(--nxt-line-height-normal);
  color: var(--nxt-fg-muted);
}

.nx-filter-bar__panel-footer {
  display: flex;
  justify-content: space-between;
  gap: var(--nxt-space-xs);
  padding-top: var(--nxt-space-xs);
  border-top: var(--nxt-hairline) solid var(--nxt-border);
}

/* ── Chips, hint, views ───────────────────────────────────────────────── */
.nx-filter-bar__active,
.nx-filter-bar__views {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--nxt-space-xs);
}

.nx-filter-bar__chip-field {
  font-weight: var(--nxt-font-weight-regular);
  color: var(--nxt-fg-muted);
}

.nx-filter-bar__hint {
  margin: 0;
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-regular);
  line-height: var(--nxt-line-height-normal);
  color: var(--nxt-fg-muted);
}

.nx-filter-bar__views-label {
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-semibold);
  color: var(--nxt-fg-muted);
}

/* ── Save dialog ──────────────────────────────────────────────────────── */
.nx-filter-bar__save {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-sm);
  width: min(var(--nxt-panel-width-xl), 92vw);
}

.nx-filter-bar__summary {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  font-size: var(--nxt-font-size-sm);
  color: var(--nxt-fg-muted);
}

.nx-filter-bar__summary-label {
  font-weight: var(--nxt-font-weight-semibold);
}

.nx-filter-bar__summary-list {
  margin: 0;
  padding-inline-start: var(--nxt-space-md);
  font-size: inherit;
  font-weight: var(--nxt-font-weight-regular);
  line-height: var(--nxt-line-height-normal);
  color: var(--nxt-fg);
}

.nx-filter-bar__sr-only {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  padding: 0;
  margin: calc(var(--nxt-hairline) * -1);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}
</style>
